import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2,
  Plug,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Unplug,
} from "lucide-react";
import { Toastify, Tabs, TabsList, TabsTrigger } from "lift-ui";
import apiClient from "@lift-admin/utils/apiClient";
import { logAdminAction } from "@lift-admin/utils/actionLogTrack";
import { getApiRoute } from "../chatbot-v2/api";

// Shared org-wide Motion (creative analytics) connection. This screen manages
// TWO independent shared connections — one per backend:
//   • LIFT AI  → lift-ai (Agno) backend, routes under /workspace/motion/*
//   • Claude   → claude-data-mcp backend, routes under /api/motion/*
// Each backend stores its own encrypted token, so the two tabs are separate
// authorizations. Connect/disconnect are admin gated; status is read-only.

// Optional local-dev overrides: point a provider's Motion calls straight at a
// locally running backend (which serves routes without the gateway prefix),
// e.g. VITE_MOTION_API_BASE=http://localhost:8000 (lift-ai) or
// VITE_CLAUDE_MOTION_API_BASE=http://localhost:8080 (claude-data-mcp). When
// unset, calls go through the normal admin base (getApiRoute → ${VITE_BASEURL}/<prefix>/...).
const LIFTAI_API_BASE = (((import.meta as any).env?.VITE_MOTION_API_BASE as string) || "")
  .trim()
  .replace(/\/$/, "");
const CLAUDE_API_BASE = (((import.meta as any).env?.VITE_CLAUDE_MOTION_API_BASE as string) || "")
  .trim()
  .replace(/\/$/, "");

type ProviderId = "liftai" | "claude";

interface MotionProvider {
  id: ProviderId;
  label: string;
  description: string;
  resolveUrl: (path: string) => string;
  paths: {
    status: string;
    connect: string;
    disconnect: string;
    connectResult: string;
  };
  connectParams: (
    email: string,
    popupId: string,
    origin: string
  ) => Record<string, string>;
  // Path (with query) for DELETE disconnect, before base resolution.
  disconnectUrl: (email: string) => string;
}

const PROVIDERS: MotionProvider[] = [
  {
    id: "liftai",
    label: "LIFT AI",
    description:
      "Connect the shared, org-wide Motion account for LIFT AI (Agno). One admin " +
      "connects once; every LIFT AI instance and agent shares this connection. " +
      "Motion data is Meta/Facebook only and read-only.",
    resolveUrl: (path) =>
      LIFTAI_API_BASE ? `${LIFTAI_API_BASE}${path}` : getApiRoute(`/liftai${path}`),
    paths: {
      status: "/workspace/motion/status",
      connect: "/workspace/motion/connect",
      disconnect: "/workspace/motion",
      connectResult: "/workspace/motion/connect-result",
    },
    connectParams: (email, popupId, origin) => ({
      admin_email: email,
      popup: "true",
      popup_id: popupId,
      json: "1",
      return_url: origin,
    }),
    disconnectUrl: (email) =>
      `/workspace/motion?admin_email=${encodeURIComponent(email)}`,
  },
  {
    id: "claude",
    label: "Claude",
    description:
      "Connect the shared, org-wide Motion account for the Claude MCP (claude-data-mcp). " +
      "One admin connects once; every Claude instance and agent shares this connection. " +
      "Motion data is Meta/Facebook only and read-only.",
    resolveUrl: (path) =>
      CLAUDE_API_BASE ? `${CLAUDE_API_BASE}${path}` : getApiRoute(`/claude-data-mcp${path}`),
    paths: {
      status: "/api/motion/status",
      connect: "/api/motion/connect",
      disconnect: "/api/motion",
      connectResult: "/api/motion/connect-result",
    },
    connectParams: (email, popupId, origin) => ({
      email,
      popup_id: popupId,
      json: "1",
      return_url: origin,
    }),
    disconnectUrl: (email) => `/api/motion?email=${encodeURIComponent(email)}`,
  },
];

interface MotionStatus {
  configured?: boolean;
  connected?: boolean;
  needs_reauth?: boolean;
  connected_by_email?: string | null;
  expires_at?: string | null;
  connected_at?: string | null;
  updated_at?: string | null;
  allowed_domain?: string | null;
}

function getAdminEmail(): string {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return parsed?.email ?? "";
  } catch {
    return "";
  }
}

function formatTs(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

// lift-ai errors surface as `detail`; claude-data-mcp as `error_description`.
function errMessage(err: any, fallback: string): string {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.error_description ||
    err?.response?.data?.error ||
    fallback
  );
}

const MotionProviderPanel: React.FC<{ provider: MotionProvider }> = ({
  provider,
}) => {
  const [status, setStatus] = useState<MotionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<number | null>(null);

  const email = getAdminEmail();

  const clearPoll = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async (): Promise<MotionStatus | null> => {
    try {
      const { data } = await apiClient.get(
        provider.resolveUrl(provider.paths.status)
      );
      setStatus(data as MotionStatus);
      return data as MotionStatus;
    } catch (err: any) {
      Toastify("error", errMessage(err, "Failed to load Motion status"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [provider]);

  // After a connect completes/closes, the DB write can land a beat after the
  // popup signal. Re-poll status a few times until it reports connected so the
  // page flips automatically without a manual refresh.
  const confirmConnected = useCallback(async (): Promise<void> => {
    for (let i = 0; i < 5; i++) {
      const s = await fetchStatus();
      if (s?.connected) return;
      await new Promise((r) => setTimeout(r, 1200));
    }
  }, [fetchStatus]);

  useEffect(() => {
    fetchStatus();
    return () => clearPoll();
  }, [fetchStatus, clearPoll]);

  // Primary completion signal: the popup posts a message back to this window.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const d: any = event.data;
      if (!d || d.type !== "lift_oauth" || d.provider !== "motion") return;
      clearPoll();
      setConnecting(false);
      if (d.connected) {
        Toastify("success", "Motion connected");
        logAdminAction("Motion integration connected", {
          url: provider.resolveUrl(provider.paths.connect),
          method: "POST",
          status: 200,
          category: "settings",
          action: "connect_motion",
          connectedByEmail: email,
        });
        confirmConnected();
      } else {
        Toastify(
          "error",
          d.error ? `Motion connection failed: ${d.error}` : "Motion connection failed"
        );
        fetchStatus();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [fetchStatus, clearPoll, confirmConnected]);

  // While a connect is in flight, refetch when the admin returns to this tab
  // (covers the popup closing without a postMessage/result signal).
  useEffect(() => {
    if (!connecting) return;
    const onFocus = () => {
      fetchStatus();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [connecting, fetchStatus]);

  const handleConnect = useCallback(async () => {
    if (!email) {
      Toastify("error", "Could not determine your admin email — please re-login.");
      return;
    }
    const popupId = `${provider.id}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    // Open the popup synchronously inside the click gesture (avoids popup
    // blockers), then navigate it to Motion's consent URL. window.open cannot
    // carry the Authorization header, so pointing the popup straight at the
    // gated connect endpoint is rejected by the gateway ("Authorization header
    // missing"). Instead fetch the consent URL via the authenticated API
    // client (json=1) and open that external URL — matching the Canva/Notion flow.
    const popup = window.open(
      "",
      "motion_oauth",
      "width=520,height=680,menubar=no,toolbar=no"
    );
    popupRef.current = popup;
    setConnecting(true);

    try {
      const { data } = await apiClient.get(
        provider.resolveUrl(provider.paths.connect),
        {
          params: provider.connectParams(email, popupId, window.location.origin),
        }
      );
      const authUrl = (data as { auth_url?: string })?.auth_url;
      if (!authUrl) throw new Error("No auth URL returned");
      if (popup) {
        popup.location.href = authUrl;
      } else {
        popupRef.current = window.open(
          authUrl,
          "motion_oauth",
          "width=520,height=680,menubar=no,toolbar=no"
        );
      }
    } catch (err: any) {
      clearPoll();
      setConnecting(false);
      try {
        popup?.close();
      } catch {
        /* ignore */
      }
      Toastify("error", errMessage(err, "Failed to start Motion connection"));
      return;
    }

    // Fallback for when postMessage is blocked (e.g. cross-origin opener): poll
    // the server-stored result and detect a manually-closed popup.
    let elapsedMs = 0;
    clearPoll();
    pollRef.current = window.setInterval(async () => {
      elapsedMs += 2000;

      if (popupRef.current && popupRef.current.closed) {
        clearPoll();
        setConnecting(false);
        confirmConnected();
        return;
      }
      try {
        const { data } = await apiClient.get(
          provider.resolveUrl(
            `${provider.paths.connectResult}?popup_id=${encodeURIComponent(popupId)}`
          )
        );
        if (data && data.pending === false) {
          clearPoll();
          setConnecting(false);
          if (data.connected) {
            Toastify("success", "Motion connected");
            logAdminAction("Motion integration connected", {
              url: provider.resolveUrl(provider.paths.connect),
              method: "POST",
              status: 200,
              category: "settings",
              action: "connect_motion",
              connectedByEmail: email,
            });
            confirmConnected();
          } else {
            if (data.error) {
              Toastify("error", `Motion connection failed: ${data.error}`);
            }
            fetchStatus();
          }
        }
      } catch {
        // ignore transient poll errors until a result lands
      }

      if (elapsedMs > 5 * 60 * 1000) {
        clearPoll();
        setConnecting(false);
      }
    }, 2000);
  }, [email, provider, fetchStatus, clearPoll, confirmConnected]);

  const handleDisconnect = useCallback(async () => {
    if (!email) {
      Toastify("error", "Could not determine your admin email — please re-login.");
      return;
    }
    if (
      !window.confirm(
        `Disconnect the shared Motion account for ${provider.label} across the entire org? ` +
          "All agents will lose Motion access until an admin reconnects."
      )
    ) {
      return;
    }
    setDisconnecting(true);
    try {
      await apiClient.delete(provider.resolveUrl(provider.disconnectUrl(email)));
      Toastify("success", "Motion disconnected");
      await logAdminAction("Motion integration disconnected (org-wide)", {
        url: provider.resolveUrl(provider.paths.disconnect),
        method: "DELETE",
        status: 200,
        category: "settings",
        action: "disconnect_motion",
        disconnectedByEmail: email,
      });
      fetchStatus();
    } catch (err: any) {
      Toastify("error", errMessage(err, "Failed to disconnect Motion"));
      await logAdminAction(
        `Failed to disconnect Motion: ${errMessage(err, err?.message || "Unknown error")}`,
        {
          url: provider.resolveUrl(provider.paths.disconnect),
          method: "DELETE",
          category: "settings",
          action: "disconnect_motion",
        },
        true
      );
    } finally {
      setDisconnecting(false);
    }
  }, [email, provider, fetchStatus]);

  const configured = status?.configured !== false; // undefined ⇒ assume configured until known
  const connected = !!status?.connected;
  const needsReauth = !!status?.needs_reauth;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-[#585755]">{provider.description}</p>
        <button
          type="button"
          onClick={fetchStatus}
          disabled={loading}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[#E5E1DF] bg-white text-[#334238] hover:bg-[#F7F4F2] disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Status card */}
      <div className="bg-[#F7F4F2] rounded-xl border border-[#E5E1DF] p-6">
        {loading ? (
          <div className="flex items-center gap-3 text-[#585755]">
            <Loader2 className="h-5 w-5 animate-spin text-[#334238]" />
            <span className="text-sm">Loading Motion status…</span>
          </div>
        ) : !configured ? (
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#B4690E] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#242424]">
                Motion is not configured on this environment
              </p>
              <p className="text-sm text-[#585755] mt-1">
                A token encryption key (MOTION_TOKEN_ENC_KEY) and the Motion
                OAuth settings must be set on the backend before the shared
                account can be connected.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              {connected ? (
                <CheckCircle2 className="h-5 w-5 text-[#2E7D32]" />
              ) : (
                <Plug className="h-5 w-5 text-[#585755]" />
              )}
              <span
                className={`text-sm font-semibold ${
                  connected ? "text-[#2E7D32]" : "text-[#585755]"
                }`}
              >
                {connected ? "Connected" : "Not connected"}
              </span>
            </div>

            {needsReauth && (
              <div className="flex items-start gap-2 rounded-lg border border-[#F0C36D] bg-[#FEF6E7] px-3 py-2">
                <AlertTriangle className="h-4 w-4 text-[#B4690E] mt-0.5" />
                <p className="text-sm text-[#7A4E00]">
                  The Motion connection needs to be re-authorized. Click
                  Reconnect to restore access.
                </p>
              </div>
            )}

            {connected && (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div>
                  <dt className="text-[#8A8783]">Connected by</dt>
                  <dd className="text-[#242424]">
                    {status?.connected_by_email || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[#8A8783]">Access token expires</dt>
                  <dd className="text-[#242424]">{formatTs(status?.expires_at)}</dd>
                </div>
                <div>
                  <dt className="text-[#8A8783]">Connected at</dt>
                  <dd className="text-[#242424]">{formatTs(status?.connected_at)}</dd>
                </div>
                <div>
                  <dt className="text-[#8A8783]">Last updated</dt>
                  <dd className="text-[#242424]">{formatTs(status?.updated_at)}</dd>
                </div>
              </dl>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleConnect}
                disabled={connecting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-[#334238] text-white hover:bg-[#2a3730] disabled:opacity-60"
              >
                {connecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plug className="h-4 w-4" />
                )}
                {connected ? "Reconnect" : "Connect Motion"}
              </button>

              {connected && (
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-[#E0B4B4] bg-white text-[#B42318] hover:bg-[#FEF3F2] disabled:opacity-60"
                >
                  {disconnecting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Unplug className="h-4 w-4" />
                  )}
                  Disconnect
                </button>
              )}
            </div>

            <p className="text-xs text-[#8A8783] pt-1">
              A popup will open for Motion consent. If it is blocked, allow
              popups for this site and try again.
              {status?.allowed_domain
                ? ` Only ${status.allowed_domain} accounts may connect.`
                : ""}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const MotionIntegration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ProviderId>("liftai");
  const activeProvider =
    PROVIDERS.find((p) => p.id === activeTab) ?? PROVIDERS[0];

  return (
    <div className="w-full h-full overflow-auto">
      <div className="max-w-[900px] mx-auto px-6 py-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-[#242424]">
            Motion Integration
          </h1>
          <p className="text-sm text-[#585755] mt-1">
            Manage the shared, org-wide Motion (creative analytics) connection
            for each backend. Each tab is an independent connection.
          </p>
        </div>

        {/* Backend tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as ProviderId)}
        >
          <TabsList className="inline-flex w-fit">
            {PROVIDERS.map((p) => (
              <TabsTrigger key={p.id} value={p.id}>
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Active provider panel — keyed so switching tabs remounts and only
            the active panel polls status. */}
        <MotionProviderPanel key={activeProvider.id} provider={activeProvider} />
      </div>
    </div>
  );
};

export default MotionIntegration;
